import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  FiUsers,
  FiShield,
  FiUser,
  FiSearch,
  FiImage,
  FiArrowDown,
  FiArrowUp,
  FiEye,
  FiFilter,
  FiUserPlus,
  FiKey,
  FiTrash2,
} from "react-icons/fi";
import { CATEGORIES, COLORS } from "../utils/constants";
import { formatCurrency, formatDate } from "../utils/helpers";
import { adminApi, parseReceiptPaths, uploadsUrl } from "../api";
import ReceiptModal from "./ReceiptModal";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  color: ${COLORS.gray800};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Card = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray200};
  border-radius: 12px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${COLORS.gray200};
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  padding: 20px 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const UserGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  padding: 20px 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const UserCard = styled.div`
  align-items: center;
  border: 1px solid ${COLORS.gray200};
  border-radius: 8px;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
`;

const Avatar = styled.div`
  align-items: center;
  background: ${COLORS.primaryLight};
  border-radius: 50%;
  color: ${COLORS.primary};
  display: flex;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  overflow: hidden;
  width: 40px;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  color: ${COLORS.gray800};
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.div`
  color: ${COLORS.gray500};
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoleBadge = styled.span`
  background: ${(props) =>
    props.$role === "admin" ? COLORS.warningLight : COLORS.primaryLight};
  border-radius: 12px;
  color: ${(props) =>
    props.$role === "admin" ? COLORS.warning : COLORS.primary};
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  text-transform: uppercase;

  &:hover {
    opacity: 0.8;
  }
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SearchWrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

const SearchIcon = styled(FiSearch)`
  color: ${COLORS.gray400};
  left: 10px;
  position: absolute;
`;

const SearchInput = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 13px;
  min-width: 0;
  padding: 8px 10px 8px 32px;
  width: 180px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SearchWrapperFull = styled(SearchWrapper)`
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const FilterSelect = styled.select`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 13px;
  padding: 8px 10px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }

  @media (max-width: 600px) {
    flex: 1;
    min-width: 0;
  }
`;

const Table = styled.div`
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  background: ${COLORS.gray50};
  border-bottom: 1px solid ${COLORS.gray200};
  color: ${COLORS.gray500};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 12px 16px;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td`
  border-bottom: 1px solid ${COLORS.gray100};
  color: ${COLORS.gray700};
  font-size: 14px;
  padding: 14px 16px;
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  background: ${(props) =>
    props.$type === "expense" ? COLORS.dangerLight : COLORS.successLight};
  border-radius: 12px;
  color: ${(props) =>
    props.$type === "expense" ? COLORS.danger : COLORS.success};
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
`;

const AmountCell = styled.span`
  color: ${(props) =>
    props.$type === "expense" ? COLORS.danger : COLORS.success};
  font-weight: 600;
`;

const ReceiptButton = styled.button`
  align-items: center;
  background: ${COLORS.primaryLight};
  border: none;
  border-radius: 4px;
  color: ${COLORS.primary};
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  gap: 4px;
  padding: 4px 8px;

  &:hover {
    background: ${COLORS.primary};
    color: ${COLORS.white};
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: ${COLORS.gray400};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 48px 24px;
`;

const Description = styled.span`
  color: ${COLORS.gray500};
  display: inline-block;
  font-size: 13px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileCards = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileCard = styled.div`
  border-bottom: 1px solid ${COLORS.gray100};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;

const MobileCardTop = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const MobileCardDate = styled.span`
  color: ${COLORS.gray500};
  font-size: 12px;
`;

const MobileCardMiddle = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const MobileCardDetail = styled.div`
  color: ${COLORS.gray500};
  font-size: 13px;
`;

const MobileCardUser = styled.span`
  color: ${COLORS.gray700};
  font-size: 13px;
  font-weight: 500;
`;

const MobileCardActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${COLORS.gray400};
  cursor: pointer;
  display: flex;
  padding: 4px;

  &:hover {
    color: ${(props) => (props.$danger ? COLORS.danger : COLORS.primary)};
  }
`;

const UserActions = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const AddUserButton = styled.button`
  align-items: center;
  background: ${COLORS.primary};
  border: none;
  border-radius: 6px;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
  padding: 8px 14px;

  &:hover {
    background: ${COLORS.primaryDark};
  }
`;

const ModalOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  inset: 0;
  justify-content: center;
  position: fixed;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  margin: 0 16px;
  max-width: 420px;
  padding: 28px;
  width: 100%;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const ModalTitle = styled.h3`
  color: ${COLORS.gray800};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ModalFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ModalLabel = styled.label`
  color: ${COLORS.gray600};
  font-size: 13px;
  font-weight: 500;
`;

const ModalInput = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  padding: 10px 12px;
  width: 100%;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const ModalSelect = styled.select`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 14px;
  padding: 10px 12px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
`;

const ModalSubmit = styled.button`
  background: ${COLORS.primary};
  border: none;
  border-radius: 6px;
  color: ${COLORS.white};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;

  &:hover {
    background: ${COLORS.primaryDark};
  }

  &:disabled {
    background: ${COLORS.gray300};
    cursor: not-allowed;
  }
`;

const ModalCancel = styled.button`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  color: ${COLORS.gray600};
  cursor: pointer;
  font-size: 14px;
  padding: 10px 20px;

  &:hover {
    background: ${COLORS.gray50};
  }
`;

const ErrorMsg = styled.div`
  background: ${COLORS.dangerLight};
  border-radius: 6px;
  color: ${COLORS.danger};
  font-size: 13px;
  padding: 10px 12px;
`;

const AdminPanel = (props) => {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [receiptPreview, setReceiptPreview] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
  });
  const [newPassword, setNewPassword] = useState("");
  const [modalError, setModalError] = useState("");

  const loadData = useCallback(async () => {
    try {
      const [usersData, txData] = await Promise.all([
        adminApi.users(),
        adminApi.transactions(),
      ]);
      setUsers(usersData);
      setTransactions(txData);
    } catch (err) {
      console.error("Failed to load admin data:", err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    const action = newRole === "admin" ? "promote to admin" : "demote to user";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;

    try {
      await adminApi.updateRole(userId, newRole);
      loadData();
    } catch (err) {
      alert("Failed to update role: " + err.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setModalError("");
    try {
      await adminApi.createUser(
        newUser.email.trim(),
        newUser.password,
        newUser.name.trim(),
        newUser.role,
      );
      setShowCreateUser(false);
      setNewUser({ name: "", email: "", password: "", role: "user" });
      loadData();
    } catch (err) {
      setModalError(err.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setModalError("");
    try {
      await adminApi.resetPassword(resetPasswordUser.id, newPassword);
      setResetPasswordUser(null);
      setNewPassword("");
      alert("Password updated successfully.");
    } catch (err) {
      setModalError(err.message);
    }
  };

  const handleDeleteUser = async (user) => {
    if (
      !window.confirm(
        `Delete user "${user.display_name}"? This will also delete all their transactions. This cannot be undone.`,
      )
    )
      return;
    try {
      await adminApi.deleteUser(user.id);
      loadData();
    } catch (err) {
      alert("Failed to delete user: " + err.message);
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await adminApi.deleteTransaction(id);
      loadData();
    } catch (err) {
      alert("Failed to delete transaction: " + err.message);
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    if (search) {
      const q = search.toLowerCase();
      const matches =
        t.payee?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.user_name?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (categoryFilter && t.category !== categoryFilter) return false;
    if (typeFilter && t.type !== typeFilter) return false;
    if (userFilter && String(t.user_id) !== userFilter) return false;
    return true;
  });

  const nonAdminUsers = users.filter((u) => u.role !== "admin");

  return (
    <Container>
      <Card>
        <CardHeader>
          <SectionTitle>
            <FiUsers
              size={18}
              style={{ verticalAlign: "middle", marginRight: 8 }}
            />
            Users ({users.length})
          </SectionTitle>
          <AddUserButton onClick={() => setShowCreateUser(true)}>
            <FiUserPlus size={14} /> Add User
          </AddUserButton>
        </CardHeader>
        <UserGrid>
          {users.map((u) => (
            <UserCard key={u.id}>
              <Avatar>{u.display_name.charAt(0).toUpperCase()}</Avatar>
              <UserInfo>
                <UserName>{u.display_name}</UserName>
                <UserEmail>{u.email}</UserEmail>
              </UserInfo>
              {u.id !== props.currentUser.id ? (
                <UserActions>
                  <RoleBadge
                    $role={u.role}
                    onClick={() => handleToggleRole(u.id, u.role)}
                    title="Click to toggle role"
                  >
                    {u.role === "admin" ? (
                      <React.Fragment>
                        <FiShield
                          size={10}
                          style={{ verticalAlign: "middle", marginRight: 2 }}
                        />
                        Admin
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <FiUser
                          size={10}
                          style={{ verticalAlign: "middle", marginRight: 2 }}
                        />
                        User
                      </React.Fragment>
                    )}
                  </RoleBadge>
                  {u.role !== "admin" && props.onImpersonate && (
                    <ActionButton
                      title="View as this user"
                      onClick={() => props.onImpersonate(u.id)}
                    >
                      <FiEye size={14} />
                    </ActionButton>
                  )}
                  <ActionButton
                    title="Reset password"
                    onClick={() => {
                      setResetPasswordUser(u);
                      setNewPassword("");
                      setModalError("");
                    }}
                  >
                    <FiKey size={14} />
                  </ActionButton>
                  <ActionButton
                    $danger
                    title="Delete user"
                    onClick={() => handleDeleteUser(u)}
                  >
                    <FiTrash2 size={14} />
                  </ActionButton>
                </UserActions>
              ) : (
                <RoleBadge $role="admin">
                  <FiShield
                    size={10}
                    style={{ verticalAlign: "middle", marginRight: 2 }}
                  />
                  You
                </RoleBadge>
              )}
            </UserCard>
          ))}
        </UserGrid>
      </Card>

      <Card>
        <CardHeader>
          <SectionTitle>
            All Transactions ({filteredTransactions.length})
          </SectionTitle>
          <Controls>
            <SearchWrapperFull>
              <SearchIcon size={14} />
              <SearchInput
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapperFull>
            <FilterSelect
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="">All Users</option>
              {nonAdminUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="expense">Expenses</option>
              <option value="replenish">Funds Added</option>
            </FilterSelect>
            <FilterSelect
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </FilterSelect>
          </Controls>
        </CardHeader>

        {filteredTransactions.length === 0 ? (
          <EmptyState>
            <FiFilter size={32} />
            <p style={{ margin: 0 }}>No transactions found.</p>
          </EmptyState>
        ) : (
          <React.Fragment>
            <Table>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>User</Th>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                    <Th>Category</Th>
                    <Th>Payee</Th>
                    <Th>Description</Th>
                    <Th>Receipt</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t) => (
                    <tr key={t.id}>
                      <Td>{formatDate(t.date)}</Td>
                      <Td>{t.user_name}</Td>
                      <Td>
                        <TypeBadge $type={t.type}>
                          {t.type === "expense" ? (
                            <React.Fragment>
                              <FiArrowDown
                                size={12}
                                style={{
                                  verticalAlign: "middle",
                                  marginRight: 2,
                                }}
                              />
                              Expense
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <FiArrowUp
                                size={12}
                                style={{
                                  verticalAlign: "middle",
                                  marginRight: 2,
                                }}
                              />
                              Funded
                            </React.Fragment>
                          )}
                        </TypeBadge>
                      </Td>
                      <Td>
                        <AmountCell $type={t.type}>
                          {t.type === "expense" ? "-" : "+"}
                          {formatCurrency(t.amount, t.currency)}
                        </AmountCell>
                      </Td>
                      <Td>{t.category}</Td>
                      <Td>{t.payee || "--"}</Td>
                      <Td>
                        <Description title={t.description}>
                          {t.description || "--"}
                        </Description>
                      </Td>
                      <Td>
                        {parseReceiptPaths(t.receipt_path).length > 0 ? (
                          <ReceiptButton
                            onClick={() =>
                              setReceiptPreview(
                                parseReceiptPaths(t.receipt_path).map(
                                  uploadsUrl,
                                ),
                              )
                            }
                          >
                            <FiImage size={14} /> View
                            {parseReceiptPaths(t.receipt_path).length > 1 &&
                              ` (${parseReceiptPaths(t.receipt_path).length})`}
                          </ReceiptButton>
                        ) : (
                          "--"
                        )}
                      </Td>
                      <Td>
                        <ActionButton
                          $danger
                          onClick={() => {
                            if (
                              window.confirm(
                                "Delete this transaction? This cannot be undone.",
                              )
                            ) {
                              handleDeleteTransaction(t.id);
                            }
                          }}
                          title="Delete transaction"
                        >
                          <FiTrash2 size={16} />
                        </ActionButton>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </Table>

            <MobileCards>
              {filteredTransactions.map((t) => (
                <MobileCard key={t.id}>
                  <MobileCardTop>
                    <TypeBadge $type={t.type}>
                      {t.type === "expense" ? (
                        <React.Fragment>
                          <FiArrowDown
                            size={12}
                            style={{
                              verticalAlign: "middle",
                              marginRight: 2,
                            }}
                          />
                          Expense
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <FiArrowUp
                            size={12}
                            style={{
                              verticalAlign: "middle",
                              marginRight: 2,
                            }}
                          />
                          Funded
                        </React.Fragment>
                      )}
                    </TypeBadge>
                    <AmountCell $type={t.type}>
                      {t.type === "expense" ? "-" : "+"}
                      {formatCurrency(t.amount, t.currency)}
                    </AmountCell>
                  </MobileCardTop>
                  <MobileCardMiddle>
                    <MobileCardUser>{t.user_name}</MobileCardUser>
                    <MobileCardDate>{formatDate(t.date)}</MobileCardDate>
                  </MobileCardMiddle>
                  <MobileCardDetail>
                    {t.category}
                    {t.payee ? ` -- ${t.payee}` : ""}
                  </MobileCardDetail>
                  {t.description && (
                    <MobileCardDetail>{t.description}</MobileCardDetail>
                  )}
                  <MobileCardActions>
                    {parseReceiptPaths(t.receipt_path).length > 0 && (
                      <ReceiptButton
                        onClick={() =>
                          setReceiptPreview(
                            parseReceiptPaths(t.receipt_path).map(uploadsUrl),
                          )
                        }
                      >
                        <FiImage size={14} /> View
                        {parseReceiptPaths(t.receipt_path).length > 1 &&
                          ` (${parseReceiptPaths(t.receipt_path).length})`}
                      </ReceiptButton>
                    )}
                    <ActionButton
                      $danger
                      onClick={() => {
                        if (
                          window.confirm(
                            "Delete this transaction? This cannot be undone.",
                          )
                        ) {
                          handleDeleteTransaction(t.id);
                        }
                      }}
                      title="Delete transaction"
                    >
                      <FiTrash2 size={16} />
                    </ActionButton>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCards>
          </React.Fragment>
        )}
      </Card>

      <ReceiptModal
        receipts={receiptPreview}
        onClose={() => setReceiptPreview([])}
      />

      {showCreateUser && (
        <ModalOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateUser(false);
          }}
        >
          <ModalCard>
            <ModalTitle>Create New User</ModalTitle>
            {modalError && <ErrorMsg>{modalError}</ErrorMsg>}
            <ModalForm onSubmit={handleCreateUser}>
              <ModalFormGroup>
                <ModalLabel>Name *</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="Full name"
                  value={newUser.name}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </ModalFormGroup>
              <ModalFormGroup>
                <ModalLabel>Username / Email *</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="Username or email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, email: e.target.value }))
                  }
                  required
                />
              </ModalFormGroup>
              <ModalFormGroup>
                <ModalLabel>Password *</ModalLabel>
                <ModalInput
                  type="password"
                  placeholder="Min 6 characters"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  required
                  minLength={6}
                />
              </ModalFormGroup>
              <ModalFormGroup>
                <ModalLabel>Role</ModalLabel>
                <ModalSelect
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, role: e.target.value }))
                  }
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </ModalSelect>
              </ModalFormGroup>
              <ModalButtons>
                <ModalCancel
                  type="button"
                  onClick={() => setShowCreateUser(false)}
                >
                  Cancel
                </ModalCancel>
                <ModalSubmit type="submit">Create User</ModalSubmit>
              </ModalButtons>
            </ModalForm>
          </ModalCard>
        </ModalOverlay>
      )}

      {resetPasswordUser && (
        <ModalOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) setResetPasswordUser(null);
          }}
        >
          <ModalCard>
            <ModalTitle>
              Reset Password for {resetPasswordUser.display_name}
            </ModalTitle>
            {modalError && <ErrorMsg>{modalError}</ErrorMsg>}
            <ModalForm onSubmit={handleResetPassword}>
              <ModalFormGroup>
                <ModalLabel>New Password *</ModalLabel>
                <ModalInput
                  type="password"
                  placeholder="Min 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </ModalFormGroup>
              <ModalButtons>
                <ModalCancel
                  type="button"
                  onClick={() => setResetPasswordUser(null)}
                >
                  Cancel
                </ModalCancel>
                <ModalSubmit type="submit">Reset Password</ModalSubmit>
              </ModalButtons>
            </ModalForm>
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AdminPanel;
